import React from "react";
import { Box, Button, type ButtonProps, forwardRef, Icon } from "@chakra-ui/react";
import { FiChevronDown, FiSliders } from "react-icons/fi";

export interface FilterButtonProps extends ButtonProps {
  /** Number of active filters; > 0 switches to the soft variant with a green dot and "Filters · n". */
  activeCount: number;
  /** Rotates the chevron and sets aria-expanded (pass the Popover's isOpen). */
  isOpen?: boolean;
}

/**
 * "Filters" trigger for the existing filter Popovers. Forwards its ref so it works directly inside <PopoverTrigger>.
 * @example <PopoverTrigger><FilterButton activeCount={n} isOpen={isOpen} /></PopoverTrigger>
 */
export const FilterButton = forwardRef<FilterButtonProps, "button">(({ activeCount, isOpen = false, children, ...rest }, ref) => {
  const active = activeCount > 0;
  return (
    <Button
      ref={ref}
      variant={active ? "soft" : "secondary"}
      aria-expanded={isOpen}
      leftIcon={
        <Box as="span" position="relative" display="inline-flex">
          <Icon as={FiSliders} boxSize="16px" aria-hidden />
          {active && (
            <Box
              as="span"
              aria-hidden
              position="absolute"
              top="-3px"
              right="-4px"
              w="8px"
              h="8px"
              borderRadius="full"
              bg="leaf.500"
              boxShadow="0 0 0 2px var(--chakra-colors-navy-50)"
            />
          )}
        </Box>
      }
      rightIcon={
        <Icon
          as={FiChevronDown}
          boxSize="16px"
          aria-hidden
          transition="transform 240ms cubic-bezier(0.2,0.8,0.2,1)"
          transform={isOpen ? "rotate(180deg)" : undefined}
        />
      }
      {...rest}
    >
      {children ?? (active ? `Filters · ${activeCount}` : "Filters")}
    </Button>
  );
});
